import {
  Avatar,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Stack,
  Text
} from '@lmc/design-system'
import { cssVars } from '@lmc/design-system/tokens'
import useResident from 'hooks/useResident'
import { NAMESPACE } from 'locale'
import { incidentReportsConstants } from 'locale/incidentReports'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { serviceUserDetails } from './serviceUserDetails'
import { useServiceUserWords } from './useServiceUserWords'

type ServiceUserDetailsCardProps = {
  residentId: string
  testId?: string
}

/** Who the case is about, fetched since the report embeds only the name. */
const ServiceUserDetailsCard = ({
  residentId,
  testId = 'incident-case-v2-service-user'
}: ServiceUserDetailsCardProps) => {
  const { t } = useTranslation(NAMESPACE.INCIDENT_REPORTS)
  const { isError, resident } = useResident({ id: residentId })
  const { singular } = useServiceUserWords()

  const header = (
    <CardHeader>
      <CardTitle weight="medium" data-testid={`${testId}-title`}>
        {t(incidentReportsConstants.CASE_V2_SERVICE_USER_DETAILS, {
          variable: singular
        })}
      </CardTitle>
    </CardHeader>
  )

  // A failed read says so; the card is left out only while it loads.
  if (isError) {
    return (
      <Card padding="16" data-testid={`${testId}-card`}>
        {header}
        <Details>
          <Text tone="secondary" data-testid={`${testId}-error`}>
            {t(incidentReportsConstants.CASE_V2_SU_LOAD_FAILED, {
              variable: singular
            })}
          </Text>
        </Details>
      </Card>
    )
  }

  if (!resident) {
    return null
  }

  const details = serviceUserDetails(t, resident)

  return (
    <Card padding="16" data-testid={`${testId}-card`}>
      {header}

      <Details>
        <Stack gap="100">
          <Identity>
            <Avatar name={details.name} src={resident.picture} size={40} />
            <Text
              variant="body-md"
              weight="medium"
              data-testid={`${testId}-name`}
            >
              {details.name}
            </Text>
          </Identity>

          {/* Sex and date of birth sit side by side, as designed. */}
          <DetailPair>
            <Detail
              testId={`${testId}-sex`}
              label={t(incidentReportsConstants.CASE_V2_SU_SEX)}
              value={details.sex}
            />
            <Detail
              testId={`${testId}-dob`}
              label={t(incidentReportsConstants.CASE_V2_SU_DATE_OF_BIRTH)}
              value={details.dateOfBirth}
            />
          </DetailPair>

          <Detail
            testId={`${testId}-nhs-number`}
            label={t(incidentReportsConstants.CASE_V2_SU_NHS_NUMBER)}
            value={details.nhsNumber}
          />
          <Detail
            testId={`${testId}-address`}
            label={t(incidentReportsConstants.CASE_V2_SU_ADDRESS)}
            value={details.address}
          />
        </Stack>
      </Details>
    </Card>
  )
}

/** Label above value; "N/A" rather than hidden, so absent reads as absent. */
const Detail = ({
  label,
  value,
  testId
}: {
  label: string
  value?: string | null
  testId: string
}) => {
  const { t } = useTranslation(NAMESPACE.INCIDENT_REPORTS)

  return (
    <Field data-testid={testId}>
      <Text variant="body-md" tone="secondary">
        {label}
      </Text>
      <Text variant="body-md" weight="medium">
        {value || t(incidentReportsConstants.CASE_V2_SU_NOT_RECORDED)}
      </Text>
    </Field>
  )
}

const { spacing } = cssVars

const Identity = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[150]};
`

/** Two fields on one line, each taking half. Stays two-up in the 328px rail. */
const DetailPair = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${spacing[200]};

  > * {
    flex: 1 1 0;
  }
`

const Field = styled.div`
  min-width: 0;
  overflow-wrap: anywhere;
`

const Details = styled(CardContent)`
  padding-top: 0;
`

export default ServiceUserDetailsCard
